import { defineStore } from 'pinia'

/**
 * CMS translation overrides, layered on top of the static `i18n/locales/*` messages. The CMS serves
 * per-locale overrides inside the `global` endpoint (`data.translations`, a flat `[{ key, value }]`
 * list); `load` fetches them, expands the dotted keys into nested messages and hands the result to
 * `mergeLocaleMessage` (see app.vue).
 *
 * Deliberately separate from the `global` store: overrides are loaded per locale and must not overwrite
 * that store's single-locale state. The shared cache key means both trigger a single request per locale.
 */
export const useTranslationStore = defineStore('translation', () => {
  const cacheStore = useCacheStore()
  const cmsFetch = useCmsFetch()

  const locale = ref('nl')
  const translations = ref<Record<string, any>>({})

  function set({ locale: newLocale, translations: newTranslations }: { locale: string; translations: Record<string, any> }) {
    locale.value = newLocale
    translations.value = newTranslations
  }

  async function load({ locale: requestedLocale }: { locale: string }) {
    const key = `global;locale:${requestedLocale}`
    const ttl = 3600
    const fetcher = async () => await cmsFetch(`/api/default/${requestedLocale}/global.json`)
    const data = await cacheStore.fetch({ key, ttl, fetcher })

    // CMS keys are flat and may be dotted (`a.b.c`); vue-i18n messages are nested, so expand them.
    const nested: Record<string, any> = {}
    for (const { key: translationKey, value } of data?.translations || []) {
      const parts = translationKey.split('.')
      let current = nested
      for (let i = 0; i < parts.length; i++) {
        if (i === parts.length - 1) {
          // A literal '@' (e.g. in an email address) is otherwise parsed as vue-i18n's linked-message
          // syntax ('@:key'). vue-i18n's own escape for a literal special character is a quoted literal
          // inside a placeholder ("{'@'}"), not backslash-escaping. CMS content authors have no way to
          // apply this themselves, so do it here; '{}'/'|'/'$'/'%' are left alone since those are in real
          // use for interpolation/pluralization.
          current[parts[i]] = typeof value === 'string' ? value.replaceAll('@', "{'@'}") : value
        } else {
          current[parts[i]] = typeof current[parts[i]] === 'object' ? current[parts[i]] : {}
          current = current[parts[i]]
        }
      }
    }

    set({ locale: requestedLocale, translations: nested })
    return nested
  }

  return { locale, translations, load }
})
